import { useCallback, useEffect, useMemo, useState } from "react";
import useTransientStore from "../data/transcientStore";
import { getContractsByAddressSystemAndUser } from "../services/MapComponentsService";
import type { ContractListItem } from "../types/UserRole";
import { toContractSelectOptions } from "../utilities/contractSelect";

interface UseUserContractsResult {
  contracts: ContractListItem[];
  options: { value: string; label: string }[];
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

export function useUserContracts(
  addressSystemId?: number,
  userId?: number,
  enabled = true,
): UseUserContractsResult {
  const storeAddressSystemId =
    useTransientStore((state) => state.selectedAddressSystem?.id) ?? 0;
  const storeUserId = useTransientStore((state) => state.currentUserId) ?? 0;

  const effectiveAddressSystemId = addressSystemId ?? storeAddressSystemId;
  const effectiveUserId = userId ?? storeUserId;

  const [contracts, setContracts] = useState<ContractListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!enabled || effectiveAddressSystemId <= 0 || effectiveUserId <= 0) {
      setContracts([]);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const result = await getContractsByAddressSystemAndUser(
        effectiveAddressSystemId,
        effectiveUserId,
      );
      setContracts(result);
    } catch (loadError) {
      console.error(loadError);
      setContracts([]);
      setError(
        loadError instanceof Error
          ? loadError.message
          : "Failed to load contracts",
      );
    } finally {
      setLoading(false);
    }
  }, [enabled, effectiveAddressSystemId, effectiveUserId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const options = useMemo(
    () => toContractSelectOptions(contracts),
    [contracts],
  );

  return { contracts, options, loading, error, reload };
}
